// Single source of truth for company-wide search entity categories (ZGroup).
// Reused by the top-bar search dropdown (SearchOption.js) and the Advanced Search page
// so labels, icons and ordering stay consistent everywhere.

// Map of backend ZGroup -> display metadata.
// ZGroups are emitted by SearchModel::get_search_list / get_search_advanced.
export const SEARCH_ENTITY_META = {
  customer: { label: "Customers", icon: "ri-group-2-fill" },
  contactperson: { label: "Contacts", icon: "ri-account-box-fill" },
  staff: { label: "Staff", icon: "ri-user-star-fill" },
  job: { label: "Jobs", icon: "ri-book-2-fill" },
  invoice: { label: "Invoices", icon: "ri-article-fill" },
  proformainvoice: { label: "Proforma", icon: "ri-article-fill" },
  proforma: { label: "Proforma", icon: "ri-article-line" },
  // Change #100203
  purchase: { label: "Purchases", icon: "ri-shopping-bag-3-fill" },
  purchasedraft: { label: "Purchase Drafts", icon: "ri-draft-fill" },
  quote: { label: "Quotes", icon: "ri-file-2-fill" },
  supplier: { label: "Suppliers", icon: "ri-account-pin-box-fill" },
  vehicle: { label: "Vehicles", icon: "ri-car-fill" },
  // Task 24836 - the company's OWN fleet, which is a different table from
  // "vehicle" above (that one belongs to a customer). Labelled Fleet rather
  // than Company Vehicles so the two are told apart at a glance in a results
  // list that may well contain both.
  companyvehicle: { label: "Fleet", icon: "ri-roadster-fill" },
  asset: { label: "Assets", icon: "ri-archive-fill" },
  task: { label: "Tasks", icon: "ri-checkbox-multiple-fill" },
  taskchecklist: { label: "Task Checklists", icon: "ri-list-check-2" },
  customersitelocation: { label: "Site Locations", icon: "ri-map-pin-fill" },
  contacttelephonenumber: { label: "Phone Numbers", icon: "ri-phone-fill" },
  foodtablereservation: { label: "Reservations", icon: "ri-calendar-fill" },
};

// Preferred order for rendering pills. Any ZGroup present in the results but missing
// from this list is appended afterwards (see helpers below); groups in this list that
// are not present in the results are simply not shown.
export const SEARCH_ENTITY_ORDER = [
  "customer",
  "contactperson",
  "staff",
  "job",
  "quote",
  "invoice",
  "proformainvoice",
  "proforma",
  "purchase",
  "purchasedraft",
  "task",
  "taskchecklist",
  "vehicle",
  "companyvehicle",
  "asset",
  "supplier",
  "customersitelocation",
  "contacttelephonenumber",
  "foodtablereservation",
];

// Fallback-safe lookup: always returns a { label, icon } object.
export const metaFor = (zgroup) =>
  SEARCH_ENTITY_META[zgroup] || {
    label: zgroup || "Other",
    icon: "ri-bubble-chart-line",
  };

// Build an ordered, de-duplicated list of the groups present in a result set,
// each with its display metadata and a count. Groups follow SEARCH_ENTITY_ORDER
// first, then any unknown groups in first-seen order.
export const groupsPresentIn = (rows) => {
  const counts = {};
  (rows || []).forEach((row) => {
    const g = row?.ZGroup;
    if (!g) return;
    counts[g] = (counts[g] || 0) + 1;
  });
  const ordered = SEARCH_ENTITY_ORDER.filter((g) => counts[g]);
  const extras = Object.keys(counts).filter(
    (g) => !SEARCH_ENTITY_ORDER.includes(g)
  );
  return [...ordered, ...extras].map((g) => ({
    group: g,
    count: counts[g],
    ...metaFor(g),
  }));
};

// Entity types that support a status filter (used by the Advanced Search page to
// enable/disable the Status control and prune incompatible pills).
export const STATUS_SUPPORTING_GROUPS = [
  "customer",
  "job",
  "invoice",
  "quote",
  "task",
];

// Entity types that support an amount filter.
export const AMOUNT_SUPPORTING_GROUPS = ["invoice", "quote", "proformainvoice"];
